/**
 * 서비스워커 등록 — **끊겨도 읽히게** 하는 쪽의 스위치.
 *
 * ## 개발 중에는 켜지 않는다
 * 서비스워커가 켜져 있으면 고친 코드가 캐시 때문에 안 보인다. 그 상태를 개발자가
 * 알아채기까지가 길다 — "왜 안 바뀌지"로 반나절을 쓴다. 그래서 **빌드본에서만** 켠다.
 *
 * ## 끄는 길을 남겨 둔다
 * 캐시가 이상해졌을 때 **사용자 쪽에서 풀 방법이 없으면** 그건 갇힌 것이다.
 * 주소 뒤에 `?sw=off` 를 붙이면 등록을 지우고 캐시를 비운다(운영 안내용 비상구).
 * 자동 복구가 아니라 **사람이 쓰는 비상구**다 — 조용히 삼키는 처리가 아니다.
 */
export function registerSw(): void {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;

  const off = new URLSearchParams(window.location.search).get("sw") === "off";
  if (off) {
    void clearCaches();
    return;
  }

  if (!import.meta.env.PROD) return;

  // load 뒤에 등록 — 첫 화면이 뜨는 것과 경쟁하지 않게
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js").catch((err: unknown) => {
      // 등록 실패는 **기능 저하**지 고장이 아니다(오프라인만 안 될 뿐 앱은 돈다).
      // 그래도 조용히 넘기지 않는다 — 왜 오프라인이 안 되는지 알 수 있어야 한다.
      console.warn("[sw] 등록 실패 — 오프라인 대비가 꺼집니다", err);
    });
  });
}

/**
 * 🔴 **비상구가 실제로 비우게 만든다** — `?sw=off`
 *
 * 예전에는 등록만 지우고 `caches.delete()` 를 불렀는데 **캐시가 안 비워졌다**.
 * 등록을 지워도 **지금 이 페이지를 제어 중인 워커는 살아 있어서**, 지우는 족족
 * 다시 채운다. 실측으로 9개 → 17개로 **늘었다**(2026-08-20).
 *
 * 그래서 지운 뒤 **주소에서 `sw=off` 를 떼고 한 번 새로고침**한다.
 * 새로 뜬 페이지는 제어하는 워커가 없으므로 캐시가 다시 차지 않는다.
 * 그 뒤 서비스워커가 새로 등록되면서 **최신 그림으로** 다시 채운다 — 그게 원하는 결과다
 * (운영자가 이걸 쓰는 이유는 "옛 그림이 계속 보인다" 이므로).
 *
 * 주소에서 `sw=off` 를 떼기 때문에 새로고침이 반복되지 않는다.
 */
async function clearCaches(): Promise<void> {
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
    if (typeof caches !== "undefined") {
      const keys = await caches.keys();
      await Promise.all(keys.filter((k) => k.startsWith("greg-")).map((k) => caches.delete(k)));
    }
  } catch (err: unknown) {
    // 지우다 실패해도 새로고침은 한다 — 반쯤 지워진 채로 두면 더 헷갈린다
    console.warn("[sw] 캐시 비우기 실패", err);
  }
  const url = new URL(window.location.href);
  url.searchParams.delete("sw");
  window.location.replace(url.toString());
}
